import { withAuthenticationRequired } from "@developertown/oidc-provider";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { pdfjs } from "react-pdf";
import { BrowserRouter, Route, Routes } from "react-router";
import ModalWrapper from "../components/Modal/ModalWrapper";
import { useModal } from "../components/Modal/useModal";
import { CustomWindow, isAuthBypassed } from "../env";
import AuthLayout from "../layouts/AuthLayout";
import Health from "../pages/Health/Health";
import Logout from "../pages/Logout/Logout";
import NoAccess from "../pages/NoAccess/NoAccess";
import PickAClient from "../pages/PickAClient/PickAClient";
import PickAPortal from "../pages/PickAPortal/PickAPortal";
import SiteUnderMaintenance from "../pages/SiteUnderMaintenance/SiteUnderMaintenance";
import BoundaryError from "../pages/SystemError/BoundaryError";
import DefaultLayoutRouter from "./DefaultLayoutRouter";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const ProtectedApp: React.FC = () => {
  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route path="/" element={<PickAClient />} />
        <Route path="no-access" element={<NoAccess />} />
        <Route path="maintenance" element={<SiteUnderMaintenance />} />
        <Route path=":client" element={<PickAPortal />} />
      </Route>
      <Route path=":client">
        {/* DEFAULT LAYOUTS */}
        <Route path="*" element={<DefaultLayoutRouter />} />
      </Route>
    </Routes>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const withAuthRequired = <T extends React.ComponentType<any>>(
  component: T,
  opts?: Parameters<typeof withAuthenticationRequired>[1],
): React.ComponentType<React.ComponentProps<T>> => {
  if (isAuthBypassed()) {
    return component;
  }
  return withAuthenticationRequired(component, opts);
};

const RequireLogin = withAuthRequired(() => null, {
  loginWithRedirectParams: () => {
    const runtime: CustomWindow = window;
    return {
      audience: runtime.env?.REACT_APP_AUTH0_AUDIENCE || "not set",
      organization: runtime.env?.REACT_APP_AUTH0_ORGANIZATION,
      connection: undefined,
    };
  },
});

const RequireLoginWithPreferredConnection = withAuthRequired(ProtectedApp, {
  loginWithRedirectParams: () => {
    const runtime: CustomWindow = window;
    return {
      extraQueryParams: {
        audience: runtime.env?.REACT_APP_AUTH0_AUDIENCE || "not set",
        organization: runtime.env?.REACT_APP_AUTH0_ORGANIZATION,
        connection: runtime.env?.REACT_APP_AUTH0_CONNECTION,
      },
    };
  },
});

const App: React.FC = () => {
  const { modal } = useModal();
  return (
    <BrowserRouter>
      <ErrorBoundary FallbackComponent={BoundaryError}>
        {modal && <ModalWrapper>{modal}</ModalWrapper>}
        <Routes>
          <Route path={"/health"} element={<Health />} />
          <Route path={"/logout"} element={<Logout />} />
          <Route path={"/login"} element={<RequireLogin />} />
          <Route path={"/*"} element={<RequireLoginWithPreferredConnection />} />
        </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default App;
